import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { ListTree } from 'lucide-react';

export interface GroupedNavItem<K extends string> {
  key: K;
  label: string;
  Icon: LucideIcon;
  /** Số đếm cạnh tên mục (vd lời mời đang chờ); 0 hoặc bỏ trống thì không hiện. */
  badge?: number;
}

export interface GroupedNavGroup<K extends string> {
  title: string;
  items: GroupedNavItem<K>[];
}

interface Props<K extends string> {
  groups: GroupedNavGroup<K>[];
  active: K;
  onSelect: (key: K) => void;
}

const Badge: React.FC<{ value?: number; active: boolean }> = ({ value, active }) =>
  value && value > 0 ? (
    <span
      className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[12px] font-semibold ${
        active ? 'bg-white/25 text-white' : 'bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300'
      }`}
    >
      {value}
    </span>
  ) : null;

/**
 * Mục lục dạng cây, cùng kiểu với mục lục trang Cửa hàng (`/store`): máy tính là cột trái dính
 * khi cuộn, hiện đủ mọi mục theo nhóm; điện thoại là các nút xuống dòng theo nhóm (không cuộn
 * ngang để người dùng thấy hết mục ngay).
 */
export function GroupedSectionNavDesktop<K extends string>({ groups, active, onSelect }: Props<K>) {
  return (
    <aside className="sticky top-[88px] hidden w-[240px] shrink-0 self-start lg:block">
      <nav className="rounded-[20px] bg-white p-3 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-slate-800 dark:shadow-none">
        <div className="flex items-center gap-2 px-3 py-2 text-[13px] font-semibold uppercase tracking-wide text-slate-400">
          <ListTree size={16} /> Mục lục
        </div>
        {groups.map((g) => (
          <div key={g.title} className="mt-2 first:mt-0">
            <div className="px-3 pb-1 pt-2 text-[12px] font-semibold text-slate-400">{g.title}</div>
            <ul className="flex flex-col gap-1">
              {g.items.map((it) => {
                const isActive = it.key === active;
                return (
                  <li key={it.key}>
                    <button
                      type="button"
                      onClick={() => onSelect(it.key)}
                      aria-current={isActive ? 'page' : undefined}
                      className={[
                        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] font-medium transition-colors',
                        isActive
                          ? 'bg-[#0057FF] text-white shadow-[0_4px_12px_rgba(0,87,255,0.35)]'
                          : 'text-[#657384] hover:bg-[#EAEFFA] dark:text-slate-300 dark:hover:bg-slate-700',
                      ].join(' ')}
                    >
                      <it.Icon size={18} className="shrink-0" />
                      <span className="truncate">{it.label}</span>
                      <Badge value={it.badge} active={isActive} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/**
 * Các nút tròn theo nhóm, hiện ở mọi cỡ màn hình — dùng khi mục lục nằm lọt trong một khung khác
 * (vd mục "Hợp tác creator" trong trang Quảng cáo), không đủ chỗ cho cột trái. `inline` xếp các nhóm
 * cạnh nhau rồi xuống dòng thay vì chồng từng nhóm một.
 */
export function GroupedSectionNavPills<K extends string>({
  groups,
  active,
  onSelect,
  inline = false,
  className = '',
}: Props<K> & { inline?: boolean; className?: string }) {
  return (
    <div className={`flex ${inline ? 'flex-wrap gap-x-6 gap-y-3' : 'flex-col gap-3'} ${className}`.trim()}>
      {groups.map((g) => (
        <div key={g.title}>
          <div className="mb-1.5 text-[12px] font-semibold text-slate-400">{g.title}</div>
          <div className="flex flex-wrap gap-2">
            {g.items.map((it) => {
              const isActive = it.key === active;
              return (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => onSelect(it.key)}
                  aria-current={isActive ? 'page' : undefined}
                  className={[
                    'flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium',
                    isActive
                      ? 'bg-[#0057FF] text-white'
                      : 'bg-[#EAEFFA] text-[#657384] dark:bg-slate-700 dark:text-slate-300',
                  ].join(' ')}
                >
                  <it.Icon size={14} />
                  {it.label}
                  {it.badge && it.badge > 0 ? <span className="font-semibold">({it.badge})</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function GroupedSectionNavMobile<K extends string>(props: Props<K>) {
  return <GroupedSectionNavPills {...props} className="ui-card p-3 lg:hidden" />;
}
